import { FileUploader as DragnDrop } from "react-drag-drop-files";

import { getMediaFileType, getMediaUrlFileType } from "Helpers/File";
import { MAX_UPLOAD_BYTES, formatBytes } from "Helpers/Upload";

import UseNotification from "Hooks/UseNotification";

import GetIcon from "Components/Global/Icons";

import * as S from "./styles";

const FileUploader = ({
  label,
  files,
  setFiles,
  allowedExtensions,
  allowMultiple,
  showFileName = false,
  loading,
  required,
  disabled,
  // Prefixo em negrito antes do nome do arquivo no link de download, para deixar
  // explícito que ele é clicável (pedido do cliente, 18/08/2026). É OPT-IN de
  // propósito: este componente serve 16 telas do admin e da área comum, e mudar
  // o padrão global não foi pedido.
  downloadLabel = null,
  // Teto por arquivo. O default vem de Helpers/Upload (500 MB desde 03/09/2026,
  // D19/D20) e vale para as 16 telas que usam este componente; a prop existe
  // para apertar caso a caso, se alguma tela precisar.
  maxSizeBytes = MAX_UPLOAD_BYTES,
  // Progresso do upload em curso (0–100). Quem faz o PUT é o chamador (via
  // UseFile), então ele repassa o percentual — sem isso um envio de 300 MB
  // parece travado e o usuário recarrega a página (D21).
  progress = null,
}) => {
  const { showNotification } = UseNotification();

  const handleChange = (fileOrFiles) => {
    const files_to_check = Array.from(
      fileOrFiles instanceof FileList ? fileOrFiles : [fileOrFiles],
    );

    if (files_to_check.length >= 6) {
      return showNotification(
        "danger",
        "Atenção",
        "Selecione no máximo 5 arquivos.",
      );
    }

    for (const file of files_to_check) {
      if (!file) return;

      if (
        !allowedExtensions.includes(file.name.split(".").pop().toLowerCase())
      ) {
        return showNotification(
          "danger",
          "Atenção",
          "Selecione um arquivo válido.",
        );
      }

      if (file.size > maxSizeBytes) {
        return showNotification(
          "danger",
          "Atenção",
          `O arquivo deve conter no máximo ${formatBytes(maxSizeBytes)}.`,
        );
      }
    }

    setFiles(allowMultiple ? files_to_check : [files_to_check[0]]);
  };

  return (
    <S.Wrapper>
      <label>
        {label} {required && <S.RequiredDot />}
      </label>
      <DragnDrop
        handleChange={handleChange}
        types={allowedExtensions}
        fileOrFiles={files}
        disabled={loading || disabled}
        multiple={allowMultiple}
      >
        <S.UpdateArea disabled={disabled}>
          {files?.length ? (
            <S.Files>
              {files.map((file, index) => {
                if (!file) return null;

                const file_name = file?.file_name
                  ? file?.file_name
                  : file?.name
                    ? file?.name
                    : "Arquivo enviado";

                return (
                  <S.UpdateTexts key={index}>
                    {file instanceof File
                      ? getMediaUrlFileType(
                          file?.file_src ? file.file_src : file,
                        )
                      : getMediaFileType(file?.file_src ? file.file_src : file)}
                    <span>
                      {showFileName ? (
                        <>
                          {file instanceof File
                            ? "Arquivo selecionado:"
                            : "Arquivo carregado:"}
                          {file_name
                            ? file_name.length > 30
                              ? `${file_name.substring(0, 30)}...`
                              : file_name
                            : ""}
                        </>
                      ) : (
                        ""
                      )}
                    </span>
                    <GetIcon
                      name="X"
                      onClick={() =>
                        setFiles(files.filter((item, i) => i !== index))
                      }
                    />
                  </S.UpdateTexts>
                );
              })}
            </S.Files>
          ) : (
            <S.UpdateTexts>
              <GetIcon name="Upload" />
              <p>
                Arraste ou selecione{" "}
                {allowMultiple ? "seus arquivos" : "um arquivo"}
              </p>
              <span>Extensões permitidas: {allowedExtensions.join(", ")}</span>
              <span>Tamanho máximo: {formatBytes(maxSizeBytes)}</span>
            </S.UpdateTexts>
          )}
        </S.UpdateArea>
      </DragnDrop>

      {/* Progresso do envio em curso (D21). Só aparece durante o upload —
          `progress` nulo é o estado normal. */}
      {progress !== null && progress >= 0 && progress < 100 ? (
        <S.Progress>
          <S.ProgressBar style={{ width: `${progress}%` }} />
          <span>{`Enviando… ${progress}%`}</span>
        </S.Progress>
      ) : null}

      <S.FileList>
        {files?.length &&
          files.map((file, index) => {
            if (!file) return null;

            const file_name = file?.file_name
              ? file?.file_name
              : file?.name
                ? file?.name
                : "Arquivo enviado";

            const file_to_open = file?.file_src ? file.file_src : file;

            return (
              <S.File key={index}>
                <a
                  onClick={() =>
                    !file?.name || file_to_open
                      ? window.open(file_to_open)
                      : null
                  }
                >
                  {/* Só para arquivo JÁ enviado: um File local recém-selecionado
                      ainda não tem o que baixar. */}
                  {downloadLabel && !(file instanceof File) ? (
                    <>
                      <strong>{downloadLabel}</strong>{" "}
                    </>
                  ) : null}
                  {file_name
                    ? file_name.length > 90
                      ? `${file_name.substring(0, 90)}...`
                      : file_name
                    : file.split("?")[0].split("/").pop()}
                </a>
                <GetIcon
                  name="X"
                  onClick={() =>
                    setFiles(files.filter((item, i) => i !== index))
                  }
                />
              </S.File>
            );
          })}
      </S.FileList>
    </S.Wrapper>
  );
};

export default FileUploader;
